"use client";
import { Button } from "@components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@components/ui/drawer";
import {
  APP_STORES_LINKS,
  DOWNLOAD_APP_POPUP_TIMEOUT,
} from "@lib/utils/consts/app-links";
import useDeviceDetection from "@lib/utils/hooks/useDeviceDetection";
import { ChevronRight, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useLayoutEffect, useState } from "react";

interface StoreLink {
  downloadText: string;
  downloadUrl: string;
}

export const MobileStoreDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [storeLink, setStoreLink] = useState<StoreLink>();
  const device = useDeviceDetection();
  const t = useTranslations("MobileStoreDialog");

  useLayoutEffect(() => {
    const informed = sessionStorage.getItem("isInformed");
    if (device?.isMobile && !informed) {
      const downloadTextKey = device?.isAndroid
        ? "googlePlayLinkText"
        : "appStoreLinkText";
      const storeLink = {
        downloadText: t(downloadTextKey),
        downloadUrl: device?.isAndroid
          ? APP_STORES_LINKS.GOOGLE_PLAY_DOWNLOAD_URL
          : APP_STORES_LINKS.APP_STORE_DOWNLOAD_URL,
      };
      setStoreLink(storeLink);
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, DOWNLOAD_APP_POPUP_TIMEOUT);
      return () => clearTimeout(timer);
    } else {
      setIsOpen(false);
    }
  }, [device, t]);

  const continueToBrowser = () => {
    sessionStorage.setItem("isInformed", "true");
  };

  return (
    <Drawer open={isOpen} onClose={continueToBrowser}>
      <DrawerContent className="bg-[#fff]">
        <DrawerHeader className="gap-0 pb-0 text-left">
          <DrawerTitle className="flex items-center justify-between py-6 text-xl">
            {t("recomendationTitle")}
            <Button
              onClick={() => setIsOpen(false)}
              size="xsm"
              className="rounded-full"
            >
              <X className="h-6 w-6" />
              <span className="sr-only">CLEAR</span>
            </Button>
          </DrawerTitle>
          <DrawerDescription className="my-6 text-primary-dark">
            {t("recomendationDescription")}
          </DrawerDescription>
        </DrawerHeader>
        <DrawerFooter className="border-[#AAAAAA] pt-0">
          <div className="flex items-center justify-between border-t border-t-grey p-lg">
            <DrawerClose>
              <a
                href={storeLink?.downloadUrl}
                className="inline-flex h-10 items-center justify-center gap-4 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-inactive disabled:text-inactive-foreground"
              >
                {storeLink?.downloadText}
              </a>
            </DrawerClose>
            <DrawerClose>
              <Button
                onClick={() => setIsOpen(false)}
                variant="link"
                className="flex gap-1 p-none text-primary-dark"
              >
                {t("continueUsingBrowser")}
                <ChevronRight strokeWidth="1" className="h-[16px] w-[16px]" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
